/** 남은 원본 보기 · 받기 · 지우기, 그리고 판으로 되돌리기의 공유 계약.
 *
 * 서버 라우트는 `apps/api/src/routes/originals.ts` 다. 저장 구조는 **새 표 · 새 칸 0건**이고
 * 마이그레이션도 0건이다(`docs/DATA_DICTIONARY.md` 51장). 읽는 칸은 이미 있는
 * `JobImage.sourcePurgedAt` · `JobImage.sourceRetentionExpiresAt`(`0011`) ·
 * `JobVersion.editStateJson` 이다.
 *
 * ⛔ **이 파일에 `Date` 산술이 없다.** 「남은 날」은 서버가 SQL(`julianday`)로 셈해 DTO 에 싣는다.
 * 이유는 `retention.ts` 머리말(Hermes 와 Workers 가 같은 DATETIME 문자열을 다르게 파싱한다)이다.
 *
 * ⛔ **화면에 나가는 낱말은 이 파일에 없다.** 코드 값과 숫자만 둔다. 문장은 PRD `7` 용어 표를
 * 따르고 `design-manager` 소관이다. **예외 하나**: `REVERT_REFUSED_COPY`(D-97). 웹과 앱이 따로 들고
 * 있다가 `JOB_DISCARDED` 글자가 갈렸기 때문에 `RevertErrorCode` 옆에 한 벌로 둔다. 글자의 정본은
 * 여전히 PRD `7.6.16` 표이고, 이 상수는 그 표를 옮긴 것이다.
 */

import type { D1DateTime } from "./retention";
import type { DetailJobView } from "./domain";
import { CONTACT_EMAIL } from "./public-copy";

/** 지금 그 작업의 원본을 지울 수 없는 사유. 셋 다 `ApiErrorCode` 에 이미 있는 글자다.
 *
 * - `ALREADY_PROCESSING`: provider 응답이 R2 에 쓰는 중이다. 그 사이에 지우면 파기가 끝난 뒤
 *   객체가 생긴다(`lib/retention-batch.ts` 의 선점 회피와 같은 이유).
 * - `PAYMENT_IN_PROGRESS`: 결제가 확정되지 않았다. 지우면 받을 것을 못 받는 결제가 생긴다.
 * - `IMAGES_UNSETTLED`: 돈이 잡힌 결제(`FUNDED_STATUSES`)가 있는데 원본이 남은 장 가운데 아직
 *   `PENDING`·`PROCESSING` 인 장이 있다. 낸 돈으로 만들 장의 밑천을 지키는 문이다. ⚠️ 이미 결판난 장
 *   (`FAILED` 의 되살리기, `DONE` 의 다시 만들기)은 이 문이 지키지 않는다. 그 장의 원본을 지우면 그
 *   두 동작은 원본이 없어 막힌다(`RETENTION_EXPIRED`). */
export type OriginalsBlockedCode = "ALREADY_PROCESSING" | "PAYMENT_IN_PROGRESS" | "IMAGES_UNSETTLED";

/** 작업 한 건의 원본 현황. `GET /api/originals` 의 한 줄이자 `GET /api/jobs/:jobId/originals` 의 본문. */
export interface JobOriginalsView {
  jobId: string;
  productName: string | null;
  /** 원본이 아직 남은 장 수(`JobImage.sourcePurgedAt IS NULL`). */
  originalCount: number;
  /** 원본이 이미 지워진 장 수(`JobImage.sourcePurgedAt IS NOT NULL`). 30일 만료 · 수동 삭제를 가르지 않는다. */
  purgedCount: number;
  /** 남은 원본 중 가장 먼저 사라지는 기한(`JobImage.sourceRetentionExpiresAt` 의 최솟값, D1 DATETIME · UTC).
   *  기한을 모르는 장(`NULL`)이 섞이면 `null` 이다. ⛔ 모르는 것을 빼고 최솟값을 내지 않는다. */
  firstExpiresAt: D1DateTime | null;
  /** 남은 원본 중 가장 늦게 사라지는 기한(최댓값). 같은 규칙으로 모르는 장이 섞이면 `null`. */
  lastExpiresAt: D1DateTime | null;
  /** `firstExpiresAt` 까지 남은 날 수. 서버가 SQL 정수 초 차(`strftime('%s')`)를 86400 으로 나눠
   *  **내림** 셈한 값이고 0 이상이다(22.1일 → 22, 0.5일 → 0, 정확히 1.0일 → 1). 0 은 「하루가 안
   *  남았다」이고 기한이 지났어도 0 이다. 지우는 기한은 길게 말하면 셀러가 말한 날에 왔을 때 이미
   *  없으므로 올리지 않는다. `firstExpiresAt` 이 `null` 이면 `null`. ⛔ 화면이 `firstExpiresAt` 을
   *  파싱해 다시 세지 않는다. */
  daysLeft: number | null;
  /** 지금 지울 수 없으면 그 사유. 지울 수 있으면 `null`. */
  deleteBlockedBy: OriginalsBlockedCode | null;
}

/** `GET /api/originals` 의 본문. */
export interface OriginalsSummaryView {
  /** 이 셀러의 남은 원본 장 수 전체. `items` 가 잘려도 이 수는 전체다. */
  totalOriginalCount: number;
  /** 원본이 한 장이라도 남은 작업만. */
  items: JobOriginalsView[];
  /** 서버 상한에 걸려 `items` 가 잘렸으면 `true`. */
  truncated: boolean;
}

/** 작업 한 건의 삭제 결과.
 *
 * - `DELETED`: 남은 원본을 지웠다.
 * - `NOTHING_TO_DELETE`: 남은 원본이 없었다(이미 지워졌거나 만료됐다). 실패가 아니다.
 * - `OriginalsBlockedCode`: 위 사유로 손대지 않았다.
 * - `NOT_FOUND`: 이 셀러의 작업이 아니거나 없다. ⛔ 남의 작업을 「없음」과 가르지 않는다.
 * - `FAILED`: R2 삭제가 실패했다. 쓰기 순서가 **R2 먼저 → D1 나중**이라 R2 에서 지우지 못한 장은
 *   `sourcePurgedAt` 이 찍히지 않고 남는다. `deletedCount` 가 0 보다 클 수 있다(일부만 지운 경우). */
export type OriginalsDeleteOutcome =
  | "DELETED"
  | "NOTHING_TO_DELETE"
  | OriginalsBlockedCode
  | "NOT_FOUND"
  | "FAILED";

export interface OriginalsDeleteResultItem {
  jobId: string;
  outcome: OriginalsDeleteOutcome;
  /** 이번 요청으로 `sourcePurgedAt` 을 찍은 장 수. */
  deletedCount: number;
}

/** `POST /api/originals/delete` 의 본문. 둘 중 하나만 보낸다(둘 다 보내면 서버가 400).
 *  `jobIds` 는 1~`ORIGINALS_DELETE_MAX_JOBS` 건. */
export type DeleteOriginalsRequest =
  | { jobIds: string[]; all?: never }
  | { all: true; jobIds?: never };

/** `POST /api/jobs/:jobId/originals/delete`(한 건 경로) 의 본문. **본문이 없거나 `{}` 이면 지금과 같다**
 *  (웹 `W-50` 은 본문 없이 부른다).
 *
 * - `onlyIfUnpaid: true`: 앱 `A-26b` 「그만두기」가 보낸다. 결제 전에 그만두는 입구라서, 그 작업에
 *   돈이 잡힌 결제(서버 `FUNDED_STATUSES`. `SETTLED_STATUSES` 에서 `REFUNDED` 를 뺀 것)가 **하나라도**
 *   있으면 지우지 않고 409 `JOB_PAID` 로 거절한다. `PC로 이어서 하기` 가 `router.push` 라 A-26 이
 *   뒤로 가기 스택에 남고, PC 에서 결제한 뒤 폰에서 그만두기를 누르면 낸 돈의 밑천이 지워지던 구멍을 닫는다.
 *   ⛔ `IMAGES_UNSETTLED` 로는 안 막힌다. 그 문은 `PENDING`·`PROCESSING` 장만 지키고, 결판난 장의
 *   원본(되살리기 · 다시 만들기의 밑천)은 지킨다고 약속하지 않는다.
 * - 타입은 `true` 만 둔다. 「끄기」를 따로 보내는 모양을 만들지 않는다. 플래그가 없으면 끈 것이다.
 *   서버는 너그럽게 읽는다. `false` 는 없는 것과 같고, 깨진 JSON · 객체가 아닌 본문 · 불리언이 아닌
 *   `onlyIfUnpaid` 는 400 이다(`routes/originals.ts` 의 본문 읽기).
 * - ⛔ 여러 건 경로(`DeleteOriginalsRequest`, `A-42`·`A-44`·`W-55`)에는 이 플래그가 없다. 그 입구는
 *   결제된 작업의 원본도 셀러가 골라 지우는 곳이다.
 *
 * 쿼리가 아니라 본문에 둔 이유: 계약이 이 파일의 타입으로 남는다. 쿼리 문자열은 타입이 없어
 * `?onlyIfUnpaid=1` · `=true` 같은 갈래를 서버가 따로 정해야 한다. */
export interface DeleteJobOriginalsRequest {
  onlyIfUnpaid?: true;
}

/** 한 건 경로가 409 로 거절할 때의 `code`. 여러 건 경로의 사유(`OriginalsBlockedCode`)에 **이 경로만의
 *  `JOB_PAID` 하나**를 더한 것이다. `ApiErrorCode` 가 열린 합집합이라 `api.ts` 를 고치지 않는다.
 *
 * - `JOB_PAID`: `DeleteJobOriginalsRequest.onlyIfUnpaid` 가 있고, 그 작업에 돈이 잡힌 결제가 있다.
 *   **플래그가 없으면 결코 나오지 않는다.**
 *
 * **판정 순서** (한 건 경로, 소유 확인 뒤):
 * 1. `onlyIfUnpaid` 이고 돈이 잡혔으면 → `JOB_PAID`. **다른 막는 코드보다 먼저다.** 이 입구는
 *    「결제 전에 그만두기」라서, 결제됐다는 사실이 처리 중 · 결제 중 · 대기 장보다 강한 사유다. 처리 중이라
 *    잠시 뒤에 다시 누르라고 답하면, 다시 눌렀을 때 결제된 작업의 원본이 지워진다.
 * 2. 그다음은 `OriginalsBlockedCode` 순서 그대로(`ALREADY_PROCESSING` → `PAYMENT_IN_PROGRESS` →
 *    `IMAGES_UNSETTLED`, 서버 `BLOCKED_SQL`).
 * 3. 막히지 않으면 지운다(R2 먼저 → D1 나중). 응답은 `DeleteOriginalsResponse` 그대로.
 *
 * 「돈이 잡혔는가」는 서버 조회 문장이 막는 사유와 **같은 한 문장**에서 이미 읽는 `funded` 다. D1 호출이
 * 늘지 않는다.
 *
 * ⛔ `OriginalsBlockedCode` 에 넣지 않은 이유: 그 타입은 `JobOriginalsView.deleteBlockedBy`(현황) ·
 * `OriginalsDeleteOutcome`(여러 건 결과 줄) · 웹 `ORIGINALS_BLOCKED_REASON` · 서버 거절 집계에 퍼져 있다.
 * 그 자리들에서 `JOB_PAID` 는 결코 나오지 않는다(현황과 여러 건 경로는 플래그를 받지 않는다). 넣으면
 * 나오지 않는 갈래를 자리마다 만들고, 현황이 「결제됐으니 못 지운다」고 말할 수 있는 것처럼 읽힌다. */
export type DeleteJobOriginalsBlockedCode = OriginalsBlockedCode | "JOB_PAID";

/** 두 삭제 경로(`deleteJob` · `deleteMany`)의 응답. 한 건 경로도 `items` 하나로 같은 모양을 쓴다. */
export interface DeleteOriginalsResponse {
  items: OriginalsDeleteResultItem[];
  /** `items[].deletedCount` 의 합. */
  deletedCount: number;
  /** `all: true` 에서 **지금 지울 수 있는 작업**이 서버 상한(100)에 걸려 일부만 돌았으면 `true`.
   *  다시 부르면 이어서 지운다. 막힌 작업은 손대지 않고 `items` 뒤에 `{ outcome: <막는 코드>,
   *  deletedCount: 0 }` 로만 실리며 이 값을 세우지 않는다. 그래서 막힌 작업만 남으면 `false` 라
   *  다시 부르기가 끝난다. */
  truncated: boolean;
}

/** 되돌리기를 거절하는 코드. `ApiErrorCode` 가 열린 합집합이라 `api.ts` 를 고치지 않고 쓸 수 있다.
 *
 * - `ALREADY_PROCESSING`: 처리 중에는 편집 상태를 바꾸지 않는다.
 * - `JOB_DISCARDED`: 원본이 파기된 작업이다(`DetailJobStatus.DISCARDED`). 기존 코드를 그대로 쓴다.
 * - `ORIGINALS_DELETED`: 그 판 스냅샷(`editStateJson.images[].imageId`)에 든 장 가운데
 *   `JobImage.sourcePurgedAt IS NOT NULL` 인 장이 **하나라도** 있다. 수동 삭제와 30일 배치는 장 쪽
 *   표시만 찍고 작업 상태를 `DISCARDED` 로 바꾸지 않으므로, `JOB_DISCARDED` 만 보면 원본을 지운
 *   뒤에도 되돌리기가 열린다(PRD 7.6.20 · 7.9 「원본을 지우면 되돌리기가 함께 죽는다」).
 *   판정 순서는 `ALREADY_PROCESSING` → `JOB_DISCARDED` → `ORIGINALS_DELETED` → `SNAPSHOT_UNREADABLE`.
 *   `DISCARDED` 작업은 이 판정에도 걸리지만 더 넓은 `JOB_DISCARDED` 가 먼저 답한다.
 *   글자는 삭제 경로(`DeleteOriginals*`)와 같은 낱말을 쓴다.
 * - ⛔ `SOURCE_PURGED` 라는 글자를 오류 코드로 쓰지 않는다. 그 글자는 `JobEvent.type` 값이라 같은
 *   글자가 두 뜻이 된다. `SOURCE_GONE`(`RecallBlockReason`) · `SOURCE_MISSING`(`FailureCode`)도
 *   이미 다른 뜻이 있어 피했다.
 * - `SNAPSHOT_UNREADABLE`: 그 판의 `editStateJson` 을 해석할 수 없다. */
export type RevertErrorCode =
  | "ALREADY_PROCESSING"
  | "JOB_DISCARDED"
  | "ORIGINALS_DELETED"
  | "SNAPSHOT_UNREADABLE"
  /** 422. 그 판의 배경이 지금 팔레트 밖이다(`ALLOWED_BACKGROUND_HEXES`). 편집 라우트와 같은 판정이고
   *  같은 글자다. 판정 순서는 `SNAPSHOT_UNREADABLE` 뒤다. */
  | "BACKGROUND_NOT_ALLOWED";

/** 되돌리기 거절 문장 다섯(PRD `7.6.16` 「되돌리기 (2026-10-27)」 표 · 목업 `A-12c`). 웹과 앱이 이 한 벌을 쓴다(D-97).
 *  ⛔ `언제든` · 기간 없는 `되돌릴 수 있습니다` · `지우셔서` · `삭제하셔서` · `지워져서` 를 쓰지 않는다. 원본을 누가 지웠는지 가를 기록이 없다. */
export const REVERT_REFUSED_COPY: Record<RevertErrorCode, string> = {
  ALREADY_PROCESSING: "지금 만드는 중이라 잠시 뒤에 됩니다.",
  JOB_DISCARDED: "이 작업의 원본 사진이 없어져 되돌릴 수 없습니다.",
  ORIGINALS_DELETED: "이 판에 들어간 장 가운데 원본 사진이 없어진 장이 있어 되돌릴 수 없습니다.",
  SNAPSHOT_UNREADABLE: `이 판의 값을 읽지 못해 되돌릴 수 없습니다. ${CONTACT_EMAIL}로 알려 주세요.`,
  BACKGROUND_NOT_ALLOWED: "이 판의 배경은 지금 고를 수 없는 색이라 되돌릴 수 없습니다.",
};

/** 되돌리기로 실제로 값이 바뀐 축. `JobVersion.editStateJson` 의 다섯 축과 한 벌이다
 *  (0002: 상품명 · 블록 · 배경 · 스펙 · 사진 순서). */
export type RevertChangedPart = "PRODUCT_NAME" | "BACKGROUND" | "BLOCKS" | "SPEC" | "PHOTO_ORDER";

/** `POST /api/jobs/:jobId/versions/:versionId/revert` 의 응답.
 *
 * ⭐ **새 판을 만들지 않는다.** 편집 상태만 그 판의 스냅샷으로 돌리고 `JobEvent(type='REVERTED')`
 * 한 줄을 남긴다. 그 줄의 `versionId` 는 비운다(`docs/DATA_DICTIONARY.md` 51장). */
export interface RevertJobResponse {
  /** 바뀐 축이 하나라도 있으면 `true`. 이미 같은 상태였으면 `false` 이고 이벤트를 남기지 않는다. */
  reverted: boolean;
  changed: RevertChangedPart[];
  job: DetailJobView;
}

/** 경로. 라우트와 한 몸이다. 라우트가 브랜치에서 빠지면 이 줄도 뺀다. */
export const originalsEndpoints = {
  summary: () => "/api/originals",
  job: (jobId: string) => `/api/jobs/${jobId}/originals`,
  /** 남은 원본 ZIP(무압축 스트리밍). R2 바인딩은 서명 URL 을 못 만들어 Worker 가 흘려 보낸다. */
  bundle: (jobId: string) => `/api/jobs/${jobId}/originals/bundle`,
  deleteJob: (jobId: string) => `/api/jobs/${jobId}/originals/delete`,
  deleteMany: () => "/api/originals/delete",
  revert: (jobId: string, versionId: string) => `/api/jobs/${jobId}/versions/${versionId}/revert`,
};

/** `DeleteOriginalsRequest.jobIds` 의 상한. 넘으면 서버가 400. */
export const ORIGINALS_DELETE_MAX_JOBS = 50;
